<!--
Directions screen
@author bmk11u / crm4f
-->
<template>
  <div id="contain" class="container directions-container">
    <Nav name="Directions" v-bind:home="true" v-if="authenticated"/>
    <Nav name="Directions" v-if="!authenticated"/>

    <CampusMap :google-api-promise="googleApiPromise"
               :pan-target="origin ? stringLatLngToNumberLatLng(origin.entrance.latLng) : null">
      <OriginDestinationMarkers
        :origin="origin"
        :destination="destination"
      />

      <DirectionsLine :google-path="directions?.overview_path"/>

    </CampusMap>


    <div id="directionsHeader">
      <h4>Directions</h4>
      <span id="directionsSeperator"  v-if="destination && destination.accessUrl" > - </span>
      <a v-if="destination && destination.accessUrl" id="accessibilityGuide" :href="destination.accessUrl" target="_blank">Accessibility
            Guide <font-awesome-icon icon="arrow-up-right-from-square"></font-awesome-icon></a>
    </div>
    <div id="directionsList">
      <ul style="font-family: PTSans,serif;" class="list-group pb-2">
        <li v-if="hasDirections" class="list-group-item"><span id="distanceLabel">Distance: </span> {{ distanceAndDuration }}</li>
        <li v-if="hasDirections" class="list-group-item">
          <img class="destination-marker" alt="origin" src="@/assets/A.svg">{{ startAddress }}
        </li>
        <li v-for="step in steps" :key="step" v-html="step" class="list-group-item"/>
        <li v-if="hasDirections" class="list-group-item">
          <img class="destination-marker" alt="destination" src="@/assets/B.svg">
          {{ endAddress }}
        </li>
      </ul>
    </div>

    <div id="pusher"></div>

    <Footer active="notifications" v-if="authenticated"/>
  </div>
</template>

<script setup>
import axios from 'axios'
import {useMapCommon} from '@/composables/MapCommon.js'
import {useLocationsCommon} from '@/composables/LocationsCommon.js'
import Footer from '@/components/FooterMenu.vue'
import {onMounted, ref} from 'vue'
import {useRoute} from 'vue-router'
import CampusMap from "@/components/map/CampusMap.vue";
import OriginDestinationMarkers from "@/components/map/OriginDestinationMarkers.vue";
import DirectionsLine from "@/components/map/DirectionsLine.vue";

const route = useRoute()
const seltab = ref(0)

// routing
const google = ref(null);
const directionsService = ref(null);
const origin = ref(null);
const destination = ref(null);
const directions = ref(null);
const accessibleRouteWaypoints = ref(null);

// written directions
const startAddress = ref(null);
const steps = ref([]);
const endAddress = ref(null);
const distanceAndDuration = ref(null);
const hasDirections = ref(true);


const {stringLatLngToNumberLatLng, fetchLocation, googleApiPromise} = useMapCommon()
const {authenticated} = useLocationsCommon()

const noRouteDataMessage = 'There\'s no route data available but the start and destination are shown on the map.';

onMounted(async () => {

  const [destinationResult, originResult, googleResult] = await Promise.allSettled([
    fetchLocation(route.query.toId),
    fetchOrigin(),
    googleApiPromise
  ]);

  destination.value = destinationResult.value;
  origin.value = originResult.value;

  google.value = googleResult.value;
  directionsService.value = new google.value.maps.DirectionsService()

  const [directionsResult, accessibleRouteWaypointsResult] = await Promise.allSettled([
    fetchDirections(false),
    fetchAccessibleRouteWaypoints()
  ]);

  directions.value = directionsResult.value;
  accessibleRouteWaypoints.value = accessibleRouteWaypointsResult.value;

  writeDirections(directions.value, origin.value.locationName, destination.value, false);
})

async function fetchOrigin() {
  if (route.query.fromId) {
    return await fetchLocation(route.query.fromId);
  }

  return {
    locationName: "Your current location",
    entrance: {latLng: {lat: route.query.geoLatitude, lng: route.query.geoLongitude}}
  };
}

async function fetchDirections(areAccessibleRoutesUsed) {
  return await generateDirections(
    stringLatLngToNumberLatLng(origin.value.entrance.latLng),
    stringLatLngToNumberLatLng(destination.value.entrance.latLng),
    directionsService.value,
    areAccessibleRoutesUsed,
    accessibleRouteWaypoints.value
  );
}

// generate directions using googles directions service
async function generateDirections(
  origin, destination, directionsService, areAccessibleRoutesUsed, accessibleRouteWaypoints) {
  let directions = null;

  await directionsService.route(
    {
      origin: origin,
      destination: destination,
      travelMode: google.value.maps.TravelMode.WALKING,
      waypoints: areAccessibleRoutesUsed ? accessibleRouteWaypoints : null
    },
    (result, status) => {
      if (status === "OK") {
        directions = result.routes[0];
      } else {
        showNoDirections();
      }
    }
  ).catch(() => {
    showNoDirections();
  });

  return directions;
}

function showNoDirections() {
  steps.value = [noRouteDataMessage];

  hasDirections.value = false;
}

// Custom directions text renderer.
function writeDirections(directions, originName, destination, useAccessibleRoutes) {
  if (!directions) {
    return null;
  }

  let distance = 0
  let duration = 0
  let currentSteps = []
  steps.value = []

  let l = 0
  // Legs loop
  directions.legs.forEach((leg) => {
    if (l === 0) {
      writeOriginAddress(leg, originName, useAccessibleRoutes)
    }
    // Steps loop
    leg.steps.forEach((step) => {
      let last = null;
      if (currentSteps.length > 0) {
        last = currentSteps[currentSteps.length - 1]
        // Group consecutive steps with the same instructions together.
        if (last.instructions === step.instructions) {
          step.distance.value += last.distance.value
          currentSteps.splice(-1)
        }
      }
      currentSteps.push(step)
      distance += step.distance.value
      duration += step.duration.value
    })
    if (l === directions.legs.length - 1) {
      writeSteps(currentSteps, useAccessibleRoutes)
      writeEndAddress(leg, destination, useAccessibleRoutes)
    }
    l += 1
  })

  distanceAndDuration.value = formatDistanceAndDuration(distance, duration, useAccessibleRoutes)
}

// Writes the start address for custom directions text renderer.
function writeOriginAddress(leg, start, useAccessibleRoutes) {
  // Add start address.
  if (useAccessibleRoutes) {
    leg.start_address = start
  } else {
    leg.start_address = 'Depart from ' + start
  }
  startAddress.value = leg.start_address
}

// Writes the steps for custom directions text renderer.
function writeSteps(stepsArg, useAccessibleRoutes) {
  stepsArg.forEach((step) => {
    let text = step.instructions
    if (text != null) {
      text = text.replace(/Walk/g, 'Head')
      text = text.replace(/walk/g, 'head')
      if (useAccessibleRoutes) {
        text = text.replace(/Take the stairs/g, '')
        text = text.replace(/take the stairs/g, '')
      }
    }
    steps.value.push(text + ' (' + formatDistance(step.distance.value) + ')')
  })
}

// Writes the end address for custom directions text renderer.
function writeEndAddress(leg, destination, useAccessibleRoutes) {
  leg.end_address = destination.locationName + ' Entrance'
  if (useAccessibleRoutes && destination.accessUrl != null) {
    leg.end_address = leg.end_address + '. See Accessibility Guide link above for building access info.'
  } else {
    leg.end_address = 'Arrive at ' + leg.end_address
    if (destination.directions != null) {
      leg.end_address = leg.end_address + '. ' + destination.directions
    }
  }
  endAddress.value = leg.end_address
}

// Formats distance and duration for custom directions text renderer.
function formatDistanceAndDuration(distance, duration, useAccessibleRoutes) {
  let distanceAndDuration = formatDistance(distance)
  if (!useAccessibleRoutes) {
    distanceAndDuration += '. '
    distanceAndDuration += formatDuration(duration)
  }
  return distanceAndDuration
}

// Formats distance for custom directions text renderer.
function formatDistance(distance) {
  if (distance < 100) {
    distance += ' m'
  } else {
    distance = distance / 1000
    distance = distance.toFixed(1)
    distance += ' km'
  }
  return distance
}

// Formats duration for custom directions text renderer.
function formatDuration(duration) {
  if (duration < 60) {
    duration = 1
  } else {
    duration = duration / 60
    duration = Math.round(duration)
  }
  if (duration === 1) {
    duration = 'About 1 min'
  } else {
    duration = 'About ' + duration + ' mins'
  }
  return duration
}

// fetches waypoints that an accessible route between origin and destination must follow
async function fetchAccessibleRouteWaypoints() {
  const isOnAccessibleRoute = origin.value.onAccessibleRoute && destination.value.onAccessibleRoute;

  if (!isOnAccessibleRoute || !areLocationsDistinct(origin.value, destination.value)) {
    return [];
  }

  // Get waypoint data
  try {
    let waypointsResponse = await axios.get(
      '/map/locations/' + route.query.fromId + '/waypoints/' + route.query.toId,
      {withCredentials: false});

    return waypointsResponse.data.slice(1, -1).map((waypoint) => {
      return {
        location: new google.value.maps.LatLng(waypoint.lat, waypoint.lng),
        stopover: true
      }
    });
  } catch (error) {
    console.log(error);
  }

  return [];
}

// true if locations are not the same based on locationId and entrance name
function areLocationsDistinct(location1, location2) {
  let areEntrancesDistinct = location1.entrance.name !== location2.entrance.name;
  let areBuildingsDistinct = location1.locationId.substring(0, 3) !== location2.locationId.substring(0, 3);

  return areEntrancesDistinct && areBuildingsDistinct;
}

</script>
<style>

.directions-container #campusMapWrapper {
    /* fallback if svh not supported */
    height: calc(100vh - 200px - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0));
}

/* Use svh if supported */
@supports(height: 100svh) {
  .directions-container #campusMapWrapper {
    /* svh to accommodate mobile browser UI */
    height: calc(100svh - 200px - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0));
  }
}

#directionsHeader {
    display:flex;
    align-items:center;
    padding: 1rem 0.5rem;
    background-color: var(--uog-blue-10);
}
#directionsHeader h4 {
    text-align: left;
    margin-bottom: 0px;
    color: var(--uog-dark-blue);
}

#directionsHeader #accessibilityGuide {
    padding:0px;
    font-size:18px;
}

#directionsSeperator {
    margin: 0px 1rem;
}

#directionsList {
  margin-bottom:2rem;
}

#directionsList ul li:first-child,
#directionsList ul li:last-child{
    border-radius:0px;
    display:flex;
}

#directionsList ul li:first-child {
  border-top: none;
}

#directionsList ul li {
    border-left:0px;
    border-right:0px;
    padding: 1rem 0px 1rem 0.5rem;
}

#directionsList .destination-marker {
    height: 20px;
    margin-right: 0.5rem;
}

#distanceLabel {
    margin-right: 5px;
}
</style>
